import type { Fate } from '.';
import { createPrng } from '@play-co/replicant';
import {
  FatePermanentStatusEffect,
  FateState,
  maxScheduleLength,
  checkStepInterval,
} from './fate.ruleset';
import features from 'src/replicant/ruleset/features';
import { PetStats } from '../pet/stats';
import {
  shouldBeAsleep,
  shouldGetDead,
  shouldGetDying,
  shouldEvolve,
  shouldWantToGoOut,
  shouldGetSick,
  shouldGetToothDecay,
  shouldGetScared,
} from './fate.status';
import {
  hasPermanentStatusEffect,
  getAttributeStatusEffect,
  getPermanentStatusEffects,
} from './fate.getters';
import { getTimeUntilAwake } from './fate.utils';

/**
 * Calculate and save new pet fate
 */
export function update(state: Fate.MutableState, now: number) {
  if (!features(state).fate) {
    return;
  }

  // Save permanent status effects from fate to pet state
  syncPermanentStatusEffects(state, now);

  // Remember start point
  const since = now;

  // Create random generation based on user id and current fateUpdateAt
  // This mean that same state and same 'now' will generate same fate
  const random = createPrng(`${state.id}-${state.fate.seed}`, now);

  // eslint-disable-next-line no-constant-condition
  while (true) {
    // Check if we sleeping first
    const sleeping = shouldBeAsleep(state, now);

    // Do not check check status effects for first 5 minutes of fate generation
    const skipPermanentStatusEffectCheck = now === since;

    // If we sleeping skip other status checks
    if (sleeping) {
      // Add sleeping time to current check time
      now += getTimeUntilAwake(now, state);
      continue; // Next iteration
    }

    // Check for max schedule size
    if (now - since > maxScheduleLength) {
      break; // Exit
    }

    // Calculate only attribute  based status effects without rnd for first interval
    if (skipPermanentStatusEffectCheck) {
      // Calculate attribute state
      const status = getAttributeStatusEffect(state, now);
      setFateStateSchedule(state, status, now);

      // Go to next iteration
      now += checkStepInterval;
      continue;
    }

    // Check death status
    const isDead = shouldGetDead(state, random(), now, since);
    if (isDead) {
      setFateStateSchedule(state, 'dead', now);
      break; // Exit
    }

    // Check dying status
    if (hasPermanentStatusEffect(state, 'dying', now)) {
      now += checkStepInterval;
      continue; // Next iteration
    }
    const isDying = shouldGetDying(state, random(), now, since);
    if (isDying) {
      setFateStateSchedule(state, 'dying', now);
      now += checkStepInterval;
      continue; // Next iteration
    }

    // Roll sick status effect
    if (hasPermanentStatusEffect(state, 'sick', now)) {
      now += checkStepInterval;
      continue; // Next iteration
    }
    const isSick = shouldGetSick(state, random(), now, since);
    if (isSick) {
      setFateStateSchedule(state, 'sick', now);
      now += checkStepInterval;
      continue; // Next iteration
    }

    // Check if we evolving
    const isEvolving =
      !hasPermanentStatusEffect(state, 'evolving', now) &&
      shouldEvolve(state, now);
    if (isEvolving) {
      setFateStateSchedule(state, 'evolving', now);
      now += checkStepInterval;
      continue; // Next iteration
    }

    // Roll wantToGoOut status effect
    if (hasPermanentStatusEffect(state, 'wantToGoOut', now)) {
      now += checkStepInterval;
      continue; // Next iteration
    }

    const isWantToGoOut = shouldWantToGoOut(state, random(), now);
    if (isWantToGoOut) {
      setFateStateSchedule(state, 'wantToGoOut', now);
      now += checkStepInterval;
      continue; // Next iteration
    }

    // Roll tooth decay status effect
    if (hasPermanentStatusEffect(state, 'toothDecay', now)) {
      now += checkStepInterval;
      continue; // Next iteration
    }
    const hasToothDecay = shouldGetToothDecay(state, random(), now, since);
    if (hasToothDecay) {
      setFateStateSchedule(state, 'toothDecay', now);
      now += checkStepInterval;
      continue; // Next iteration
    }

    // Roll scared status effect
    if (hasPermanentStatusEffect(state, 'scared', now)) {
      now += checkStepInterval;
      continue; // Next iteration
    }
    const isScared = shouldGetScared(state, random(), now, since);
    if (isScared) {
      setFateStateSchedule(state, 'scared', now);
      now += checkStepInterval;
      continue; // Next iteration
    }

    // Calculate attribute state
    const status = getAttributeStatusEffect(state, now);
    setFateStateSchedule(state, status, now);
    // Go to next iteration
    now += checkStepInterval;
  }
}

/**
 * Save current fate state to pet state
 */
export function syncPermanentStatusEffects(
  state: Fate.MutableState,
  now: number,
) {
  const effects = getPermanentStatusEffects(state, now);

  const effectsIds = Object.keys(effects) as FatePermanentStatusEffect[];

  for (let index = 0; index < effectsIds.length; index++) {
    const id = effectsIds[index];
    const status = effects[id];

    // We don't want to save sleeping into state
    // Sleeping it just virtual state than real
    if (id === 'asleep') continue;
    // Save status effect in state

    resolveStatusEffect(state, id, status.value, status.timestamp);
  }

  // Clear old fate calculation
  state.fate.schedule = {};
  state.fate.seed = now;
}

/**
 * Set state value
 */
export function setFateStateSchedule(
  state: Fate.MutableState,
  effect: FateState,
  now: number,
) {
  // Already exist
  if (state.fate.schedule[effect]) return;

  state.fate.schedule[effect] = {
    timestamp: now,
  };
}

/**
 * Remove status effect from fate
 */
export function resolveStatusEffect(
  state: Fate.MutableState,
  effect: FatePermanentStatusEffect,
  hasStatus: boolean,
  now: number,
) {
  const { setStatusEffect } = PetStats.modifiers;
  const statusEffect = state.pet.statusEffects[effect];
  const oldValue = statusEffect.value;
  const isFateStatusEffectActive = !!state.fate.schedule[effect];

  // Update lastUpdatedAt only if value was changed
  if (oldValue !== hasStatus || isFateStatusEffectActive !== hasStatus) {
    setStatusEffect(state, effect, hasStatus, now);
  }

  if (hasStatus === false) {
    delete state.fate.schedule[effect];
  }
}

/**
 * Temporary death feature start at timestamp
 * This will be set on login action and from this time fate will schedule death
 * Any fate update messages that will be process before this timestamp will use previous approach with death.
 * See more: https://playcoinc.slack.com/archives/C031L3JJ8HK/p1663112985321139
 */
export function activateDeathFeature(state: Fate.MutableState, now: number) {
  state.fate.deathFeatureActivationAt = now;
}
